import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Book, EpigraphSettings } from '../types';
import { getGraphQLClient, GET_BOOK, UPDATE_BOOK } from '../lib/graphql';
import { captureException } from '../lib/sentry';
import { FrontMatterEditorLayout } from '../components/front-matter/FrontMatterEditorLayout';
import {
  getEpigraphTemplates,
  getEpigraphTemplate,
  EpigraphTemplate,
} from '../lib/frontMatterTemplates';
import { FormLabel } from '../components/common';

export default function EpigraphEditorPage() {
  const { t } = useTranslation();
  const { bookId } = useParams<{ bookId: string }>();
  const [book, setBook] = useState<Book | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [isVisible, setIsVisible] = useState(false);
  const [quote, setQuote] = useState('');
  const [attribution, setAttribution] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  useEffect(() => {
    const loadBook = async () => {
      if (!bookId) return;

      try {
        const client = getGraphQLClient();
        const data = await client.request<{ book: Book }>(GET_BOOK, { id: bookId });
        setBook(data.book);

        // Initialize form state from book settings
        const settings = data.book.frontMatterSettings?.epigraph;
        setIsVisible(settings?.visible ?? false);
        setQuote(settings?.quote ?? '');
        setAttribution(settings?.attribution ?? '');
        setSelectedTemplateId(settings?.templateId ?? null);
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
      } finally {
        setIsLoading(false);
      }
    };

    loadBook();
  }, [bookId, t]);

  const handleSave = async () => {
    if (!bookId) return;

    setIsSaving(true);
    setError(null);

    try {
      const client = getGraphQLClient();
      const epigraphSettings: EpigraphSettings = {
        visible: isVisible,
        quote: quote || undefined,
        attribution: attribution || undefined,
        templateId: selectedTemplateId || undefined,
      };

      await client.request(UPDATE_BOOK, {
        id: bookId,
        frontMatterSettings: {
          epigraph: epigraphSettings,
        },
      });
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleTemplateSelect = (template: EpigraphTemplate) => {
    setQuote(t(template.quoteKey));
    setAttribution(t(template.attributionKey));
    setSelectedTemplateId(template.id);
  };

  const handleQuoteChange = (newQuote: string) => {
    setQuote(newQuote);
    // Clear template selection if user edits
    if (selectedTemplateId) {
      const template = getEpigraphTemplate(selectedTemplateId);
      if (template && newQuote !== t(template.quoteKey)) {
        setSelectedTemplateId(null);
      }
    }
  };

  const handleAttributionChange = (newAttribution: string) => {
    setAttribution(newAttribution);
    // Clear template selection if user edits
    if (selectedTemplateId) {
      const template = getEpigraphTemplate(selectedTemplateId);
      if (template && newAttribution !== t(template.attributionKey)) {
        setSelectedTemplateId(null);
      }
    }
  };

  const templates = book ? getEpigraphTemplates(book.bookType) : [];

  return (
    <FrontMatterEditorLayout
      title={t('frontMatter.epigraph')}
      bookId={bookId!}
      isLoading={isLoading}
      isSaving={isSaving}
      isVisible={isVisible}
      onVisibilityChange={setIsVisible}
      onSave={handleSave}
      error={error}
      preview={
        <div className="text-center max-w-lg">
          {quote ? (
            <blockquote>
              <p className="font-serif text-lg italic text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                &ldquo;{quote}&rdquo;
              </p>
              {attribution && (
                <footer className="text-sm text-gray-500 dark:text-gray-500">
                  &mdash; {attribution}
                </footer>
              )}
            </blockquote>
          ) : (
            <p className="text-gray-400 dark:text-gray-500 italic">{t('frontMatter.noEpigraph')}</p>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        {/* Template selector */}
        <div>
          <FormLabel>{t('frontMatter.templates.title')}</FormLabel>
          <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
            {templates.map((template) => (
              <button
                key={template.id}
                onClick={() => handleTemplateSelect(template)}
                className={`text-left px-4 py-3 rounded-lg border transition-colors ${
                  selectedTemplateId === template.id
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-1">
                  &ldquo;{t(template.quoteKey)}&rdquo;
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  &mdash; {t(template.attributionKey)}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Custom quote */}
        <div>
          <FormLabel>{t('frontMatter.quote')}</FormLabel>
          <textarea
            value={quote}
            onChange={(e) => handleQuoteChange(e.target.value)}
            placeholder={t('frontMatter.quotePlaceholder')}
            rows={4}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Attribution */}
        <div>
          <FormLabel>{t('frontMatter.attribution')}</FormLabel>
          <input
            type="text"
            value={attribution}
            onChange={(e) => handleAttributionChange(e.target.value)}
            placeholder={t('frontMatter.attributionPlaceholder')}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>
    </FrontMatterEditorLayout>
  );
}
